import { defineComponent as _defineComponent } from 'vue'
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcCardVariable, {
  CardVariableProps,
} from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardVariable.ts.vue';
import CcConfirmModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcConfirmModal.ts.vue';
import CcWidgetCard from '@/views/Automation/CampaignCreatorV2/components/Widgets/CcWidgetCard.ts.vue';
import CcPreviewAdCard, {
  AdPreviewProps,
} from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewAdCard.ts.vue';
import CcPreviewCard from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewCard.ts.vue';
import CcPreviewPlaceholder from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewPlaceholder.ts.vue';
import CcPreviewTabs from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewTabs.ts.vue';
import useAbGroups from '@/views/Automation/CampaignCreatorV2/store/AbGroups';
import useAdStore from '@/views/Automation/CampaignCreatorV2/store/Ad';
import useAdSetStore from '@/views/Automation/CampaignCreatorV2/store/AdSet';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import useNavigationStore from '@/views/Automation/CampaignCreatorV2/store/Navigation';
import { MainEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { CardLayer, Level, PreviewTab, Tab } from '@/views/Automation/CampaignCreatorV2/types';
import { AdDTO } from '@/views/Automation/CampaignCreatorV2/validation/AdDTO';
import { AdSetDTO } from '@/views/Automation/CampaignCreatorV2/validation/AdSetDTO';
import { CampaignDTO } from '@/views/Automation/CampaignCreatorV2/validation/CampaignDTO';
import { chunk } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CcPreview.ts',
  setup(__props) {

const activeTab = ref(PreviewTab.MY_SPACE);
const selectedMySpaceTab = ref(PreviewTab.TEMPLATES);

const campaignStore = useCampaignStore();
const campaignGetter = storeToRefs(campaignStore);

const adsetStore = useAdSetStore();
const adsetGetters = storeToRefs(adsetStore);

const navigationSore = useNavigationStore();
const navigationGetters = storeToRefs(navigationSore);

const adStore = useAdStore();
const adGetters = storeToRefs(adStore);

const abStore = useAbGroups();

const abGenerated = computed(() => {
  const entity = navigationGetters.getCurrentLevel.value;

  switch (entity) {
    case Level.AdSet: {
      const previews = adsetGetters.getResults.value;
      return previews.map(AdSetDTO.variationPreview);
    }
    case Level.Campaign: {
      const previews = campaignGetter.getResults.value;
      return previews.map(CampaignDTO.variationPreview);
    }
    case Level.Ad: {
      const previews = adGetters.getResults.value;
      return previews.map(AdDTO.variationPreview);
    }
    default:
      return [];
  }
});

// Templates Modal
const isApplyTemplatesModalOpened = ref(false);
const isDeleteTemplatesModalOpened = ref(false);
const selectedTemplateId = ref('');

// AdSet Suggestion Modal
const isAdSetSuggestionsModalOpened = ref(false);
const selectedSuggestedAdSetId = ref('');

// Ad Suggestion Modal
const isAdSuggestionsModalOpened = ref(false);
const selectedSuggestedAdId = ref('');

const isTemplatesLoading = computed(() => campaignGetter.templatesLoading.value);
const isSuggestionsAdSetLoading = computed(() => adsetGetters.isSuggestionsLoading.value);
const isSuggestionsAdLoading = computed(() => adGetters.isSuggestionsLoading.value);

const templates = computed(() => campaignGetter.getTemplatesPreview.value);

const campaignTasks = computed(() => campaignGetter.getComputedCampaignForCreation.value.campaignTaskForCreation);

const campaignMetrics = computed(() => {
  const taskMetrics = campaignTasks.value?.map((t) => t.getAllMetrics()).flat() ?? [];
  return CampaignDTO.reduceMetrics(taskMetrics);
});

const disableStatusChange = computed(() => abGenerated.value?.map((e) => e.active)?.filter(Boolean)?.length === 1);

const tabs: Tab[] = [
  {
    title: 'My Space',
    id: PreviewTab.MY_SPACE,
  },
  {
    title: 'Variation Preview',
    id: PreviewTab.AD_SET_PREVIEW,
    popover: {
      position: 'icon',
      content:
        'In this section you will see a list of all campaign variations that have been generated based on the different targeting options. <br> For example if you have specified 3 different budget variants, each variant will generate a separate campaign variation.',
    },
  },
];

const mySpaceTabs = computed<Tab[]>(() => {
  switch (navigationGetters.currentLevel.value) {
    case Level.Campaign:
      return [
        {
          title: 'Templates ',
          id: PreviewTab.TEMPLATES,
        },
      ];
    case Level.AdSet:
      return [
        {
          title: 'Suggestions',
          id: PreviewTab.SUGGESTIONS,
        },
      ];
    case Level.Ad:
      return [
        {
          title: 'Ad Suggestions',
          id: PreviewTab.SUGGESTIONS,
          popover: {
            content: 'Recently used ads or suggestions based on your campaign and targeting.',
            position: 'icon',
          },
        },
      ];
    default:
      return [];
  }
});

const activeMySpaceTabs = computed(() => {
  const tab = mySpaceTabs.value.find((e) => e.id === selectedMySpaceTab.value) || mySpaceTabs.value[0];
  return tab.id;
});

const adSetSuggestions = computed(() => {
  const adsetSuggestionsValue = adsetGetters.getSuggestions.value;
  const Calculate = adsetSuggestionsValue.map((s) => ({
    id: s.id,
    name: s.name,
    variables: Object.keys(s.variables).map((v): CardVariableProps => {
      const variable = s.variables[v];

      return {
        id: `${s.id}-${variable.field}`,
        ...AdSetDTO.variablePreview(variable),
      };
    }),
  }));
  return Calculate;
});

const adSuggestions = computed(() => {
  const suggestions = adGetters.getSuggestions;

  const mapped = suggestions.value.map(
    (suggestion): AdPreviewProps & { id: string; metrics: Array<{ name: string; value: string }> } => {
      return {
        id: suggestion.id,
        call_to_action_type: AdDTO.variablePreview(suggestion.variables?.call_to_action_type)?.value,
        body: AdDTO.variablePreview(suggestion.variables?.body)?.value,
        displayLink: 'YourURL.com',
        headline: AdDTO.variablePreview(suggestion.variables?.headline)?.value,
        image_url: suggestion.variables.image.value.image_url,
        title: AdDTO.variablePreview(suggestion.variables?.title)?.value,
        metrics: suggestion.metrics,
      };
    }
  );
  return chunk(mapped, 2);
});

function templatesActionClick(action: string, suggestionId: string) {
  selectedTemplateId.value = suggestionId;

  switch (action) {
    case 'applySettings':
      isApplyTemplatesModalOpened.value = true;
      break;
    case 'removeSettings':
      isDeleteTemplatesModalOpened.value = true;
      break;
  }
}

function applyTemplate(id: string) {
  isApplyTemplatesModalOpened.value = false;
  campaignStore.applyTemplate(id);
}

function removeTemplate(id: string) {
  campaignStore.deleteTemplate(id);
  isDeleteTemplatesModalOpened.value = false;
}

function applySuggestionAdSet(id: string) {
  isAdSetSuggestionsModalOpened.value = false;
  adsetStore.applySuggestions(id);
}

function adSetSuggestionActionClick(action: string, suggestionId: string) {
  selectedSuggestedAdSetId.value = suggestionId;

  switch (action) {
    case 'applySettings':
      isAdSetSuggestionsModalOpened.value = true;
      break;
  }
}

function applySuggestionAd(id: string) {
  isAdSuggestionsModalOpened.value = false;
  adStore.applySuggestions(id);
}

function changeStatus(value: { status: boolean; movingVariables: string[]; entity: MainEntities }) {
  if (abGenerated.value.length === 1) {
    // TODO - UI message needed here ?
    console.log('You can not pause the only variation', abGenerated.value);
    return;
  }

  if (!value.status) {
    abStore.pauseAbVariation(value.entity, value.movingVariables);
  } else {
    abStore.startAbVariation(value.entity, value.movingVariables);
  }
}

function onActiveTabChange(id: PreviewTab) {
  activeTab.value = id;
}

function onMySpaceTabChange(id: PreviewTab) {
  selectedMySpaceTab.value = id;
}

return { __sfc: true,activeTab, selectedMySpaceTab, campaignStore, campaignGetter, adsetStore, adsetGetters, navigationSore, navigationGetters, adStore, adGetters, abStore, abGenerated, isApplyTemplatesModalOpened, isDeleteTemplatesModalOpened, selectedTemplateId, isAdSetSuggestionsModalOpened, selectedSuggestedAdSetId, isAdSuggestionsModalOpened, selectedSuggestedAdId, isTemplatesLoading, isSuggestionsAdSetLoading, isSuggestionsAdLoading, templates, campaignTasks, campaignMetrics, disableStatusChange, tabs, mySpaceTabs, activeMySpaceTabs, adSetSuggestions, adSuggestions, templatesActionClick, applyTemplate, removeTemplate, applySuggestionAdSet, adSetSuggestionActionClick, applySuggestionAd, changeStatus, onActiveTabChange, onMySpaceTabChange, CcCardItem, CcCardVariable, CcConfirmModal, CcWidgetCard, CcPreviewAdCard, CcPreviewCard, CcPreviewPlaceholder, CcPreviewTabs, CardLayer, Level, PreviewTab, chunk }
}

})